import { DashboardLayout } from 'components/layout/DashboardLayout';
import { GeneralTable } from 'components/tables/GeneralTable';
import { useCustomSelector } from 'hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { useTableColumns } from './hooks/useTableColumns';

import "assets/scss/billingPortal/modules/reporting/apiUsage.scss";
import { func_file, getTotalCountFromHeader, isArrayEmpty, isArrayNotEmpty } from 'functions';
import useReporting from 'dataflow/billingPortal/actions/useReporting.actions';
import useAccountControlActions from 'dataflow/billingPortal/actions/useAccountControl.actions';
import { Dropdown } from 'components/filters/Dropdown';
import { DateRangePicker } from 'components/form/DateRangePicker';
import { Button } from 'components/buttons/Button';
import { Toaster } from 'components/notification/Toaster';
import { SubmenuTooltip } from 'components/informational/SubmenuTooltip';
import { MoreButton } from 'components/buttons/MoreButton';
import ic_export from 'assets/images/ic_export.svg';
import moment from 'moment';

export default function ApiUsage() {
    const [currentView, setCurrentView] = useState("filter");
    const [selectedCompanyId, setSelectedCompanyId] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [detailType, setDetailType] = useState("");
    const [detailEntityType, setDetailEntityType] = useState("");
    const [detailCompanyId, setDetailCompanyId] = useState("");
    const [detailList, setDetailList] = useState([]);

    const {
        getApiUsageReport_requesting, apiUsageReportList, apiUsageReportListHeaders
    } = useCustomSelector("reporting");

    const { allComList } = useCustomSelector("accountControl");

    const {
        userPermissions
    } = useCustomSelector("userManagementReducers");

    const {
        getApiUsageReport
    } = useReporting();

    const { getAllComList } = useAccountControlActions();

    useEffect(() => {
        getAllComList();
    }, []);

    const normalizedApiUsageList = useMemo(() => {
        if (!isArrayNotEmpty(apiUsageReportList)) return [];

        return apiUsageReportList.map((item = {}) => {
            const companyId = item?.CompanyId ?? item?.companyId;
            const numberOfClientId = item?.NumberOfClientId ?? item?.numberOfClientId ?? 0;
            const numberOfActiveClientId = item?.NumberOfActiveClientId ?? item?.numberOfActiveClientId ?? 0;
            const numberOfInactiveClientId = item?.NumberOfInactiveClientId ?? item?.numberOfInactiveClientId ?? (numberOfClientId - numberOfActiveClientId);

            const numberOfAppIdToken = item?.NumberOfAppIdToken ?? item?.numberOfAppIdToken ?? 0;
            const numberOfActiveAppIdToken = item?.NumberOfActiveAppIdToken ?? item?.numberOfActiveAppIdToken ?? 0;
            const numberOfInactiveAppIdToken = item?.NumberOfInactiveAppIdToken ?? item?.numberOfInactiveAppIdToken ?? (numberOfAppIdToken - numberOfActiveAppIdToken);

            const activeClientIdInfo = isArrayNotEmpty(item?.ActiveClientIdInfo)
                ? item.ActiveClientIdInfo
                : (isArrayNotEmpty(item?.activeClientIdInfo) ? item.activeClientIdInfo : []);

            const deletedClientIdInfo = isArrayNotEmpty(item?.DeletedClientIdInfo)
                ? item.DeletedClientIdInfo
                : (isArrayNotEmpty(item?.deletedClientIdInfo) ? item.deletedClientIdInfo : []);

            const activeAppIdTokenInfo = isArrayNotEmpty(item?.ActiveAppIdTokenInfo)
                ? item.ActiveAppIdTokenInfo
                : (isArrayNotEmpty(item?.activeAppIdTokenInfo) ? item.activeAppIdTokenInfo : []);

            const deletedAppIdTokenInfo = isArrayNotEmpty(item?.DeletedAppIdTokenInfo)
                ? item.DeletedAppIdTokenInfo
                : (isArrayNotEmpty(item?.deletedAppIdTokenInfo) ? item.deletedAppIdTokenInfo : []);

            return {
                CompanyId: companyId,
                NumberOfClientId: numberOfClientId,
                NumberOfActiveClientId: numberOfActiveClientId,
                NumberOfInactiveClientId: numberOfInactiveClientId,
                NumberOfAppIdToken: numberOfAppIdToken,
                NumberOfActiveAppIdToken: numberOfActiveAppIdToken,
                NumberOfInactiveAppIdToken: numberOfInactiveAppIdToken,
                ActiveClientIdInfo: activeClientIdInfo.map((client = {}) => ({
                    ProjectName: client?.ProjectName ?? client?.projectName ?? "-",
                    ClientId: client?.ClientId ?? client?.clientId ?? "-",
                    CreatedDate: client?.CreatedDate ?? client?.createdDate,
                })),
                DeletedClientIdInfo: deletedClientIdInfo.map((client = {}) => ({
                    ProjectName: client?.ProjectName ?? client?.projectName ?? "-",
                    ClientId: client?.ClientId ?? client?.clientId ?? "-",
                    CreatedDate: client?.CreatedDate ?? client?.createdDate,
                    DeletedDate: client?.DeletedDate ?? client?.deletedDate,
                })),
                ActiveAppIdTokenInfo: activeAppIdTokenInfo.map((token = {}) => ({
                    TokenId: token?.TokenId ?? token?.tokenId,
                    Version: token?.Version ?? token?.version,
                    ProjectName: token?.ProjectName ?? token?.projectName ?? "-",
                    ApplicationName: token?.ApplicationName ?? token?.applicationName ?? "-",
                    CompanyName: token?.CompanyName ?? token?.companyName ?? "-",
                    DeveloperName: token?.DeveloperName ?? token?.developerName ?? "-",
                    CreatedDate: token?.CreatedDate ?? token?.createdDate,
                })),
                DeletedAppIdTokenInfo: deletedAppIdTokenInfo.map((token = {}) => ({
                    TokenId: token?.TokenId ?? token?.tokenId,
                    Version: token?.Version ?? token?.version,
                    ProjectName: token?.ProjectName ?? token?.projectName ?? "-",
                    ApplicationName: token?.ApplicationName ?? token?.applicationName ?? "-",
                    CompanyName: token?.CompanyName ?? token?.companyName ?? "-",
                    DeveloperName: token?.DeveloperName ?? token?.developerName ?? "-",
                    CreatedDate: token?.CreatedDate ?? token?.createdDate,
                })),
            };
        });
    }, [apiUsageReportList]);

    function openActiveClientDetails(rowData) {
        const activeClientIdInfo = rowData?.ActiveClientIdInfo;
        if (!isArrayNotEmpty(activeClientIdInfo)) {
            Toaster({ type: "info", msg: "No active client details available" });
            return;
        }

        setDetailEntityType("client");
        setDetailType("active");
        setDetailCompanyId(rowData?.CompanyId ?? "");
        setDetailList(activeClientIdInfo);
        setCurrentView("details");
    }

    function openInactiveClientDetails(rowData) {
        const deletedClientIdInfo = rowData?.DeletedClientIdInfo;
        if (!isArrayNotEmpty(deletedClientIdInfo)) {
            Toaster({ type: "info", msg: "No inactive client details available" });
            return;
        }

        setDetailEntityType("client");
        setDetailType("inactive");
        setDetailCompanyId(rowData?.CompanyId ?? "");
        setDetailList(deletedClientIdInfo);
        setCurrentView("details");
    }

    function openActiveAppIdTokenDetails(rowData) {
        const activeAppIdTokenInfo = rowData?.ActiveAppIdTokenInfo;
        if (!isArrayNotEmpty(activeAppIdTokenInfo)) {
            Toaster({ type: "info", msg: "No active App ID token details available" });
            return;
        }

        setDetailEntityType("appIdToken");
        setDetailType("active");
        setDetailCompanyId(rowData?.CompanyId ?? "");
        setDetailList(activeAppIdTokenInfo);
        setCurrentView("details");
    }

    function openInactiveAppIdTokenDetails(rowData) {
        const deletedAppIdTokenInfo = rowData?.DeletedAppIdTokenInfo;
        if (!isArrayNotEmpty(deletedAppIdTokenInfo)) {
            Toaster({ type: "info", msg: "No inactive App ID token details available" });
            return;
        }

        setDetailEntityType("appIdToken");
        setDetailType("inactive");
        setDetailCompanyId(rowData?.CompanyId ?? "");
        setDetailList(deletedAppIdTokenInfo);
        setCurrentView("details");
    }

    const columns = useTableColumns({
        onActiveClientClick: openActiveClientDetails,
        onInactiveClientClick: openInactiveClientDetails,
        onActiveAppIdTokenClick: openActiveAppIdTokenDetails,
        onInactiveAppIdTokenClick: openInactiveAppIdTokenDetails,
    });

    function formatDate(value) {
        return moment(value).isValid() ? moment(value).format("DD-MM-YYYY") : "-";
    }

    function handleClosePage() {
        if (currentView === "details") {
            setCurrentView("summary");
            return;
        }

        if (currentView === "summary") {
            setCurrentView("filter");
        }
    }

    function exportDetailTable() {
        if (!isArrayNotEmpty(detailList)) {
            Toaster({ type: "info", msg: "No detail rows to export" });
            return;
        }

        const isInactiveDetails = detailType === "inactive";
        const isClientDetails = detailEntityType === "client";

        const headers = isClientDetails
            ? [[
                "Project Name",
                "Client ID",
                "Created Date",
                ...(isInactiveDetails ? ["Deleted Date"] : []),
            ]]
            : [[
                "Token ID",
                "Version",
                "Project Name",
                "Application Name",
                "Company Name",
                "Developer Name",
                "Created Date",
            ]];

        const rows = detailList.map((item = {}) => {
            if (isClientDetails) {
                return [
                    item?.ProjectName || "",
                    item?.ClientId || "",
                    formatDate(item?.CreatedDate),
                    ...(isInactiveDetails ? [formatDate(item?.DeletedDate)] : []),
                ];
            }

            return [
                item?.TokenId ?? "",
                item?.Version ?? "",
                item?.ProjectName || "",
                item?.ApplicationName || "",
                item?.CompanyName || "",
                item?.DeveloperName || "",
                formatDate(item?.CreatedDate),
            ];
        });

        const columnWidths = headers[0].map((header) => ({ wch: Math.max(header.length, 20) }));

        const fileNamePrefix = isClientDetails
            ? (isInactiveDetails ? "Inactive_Client_Id_Details" : "Active_Client_Id_Details")
            : (isInactiveDetails ? "Inactive_App_Id_Token_Details" : "Active_App_Id_Token_Details");
        const fileName = `${fileNamePrefix}_${detailCompanyId || "Company"}_${moment().format("YYYYMMDD_HHmmss")}`;

        func_file.generateFile_XLSX(fileName, "Api Usage Details", headers, rows, null, null, columnWidths);
        Toaster({ type: "success", msg: "Successfully exported detail report" });
    }

    function getDetailTitle() {
        if (detailEntityType === "client") {
            return detailType === "inactive" ? "Inactive Client ID Details" : "Active Client ID Details";
        }

        if (detailEntityType === "appIdToken") {
            return detailType === "inactive" ? "Inactive App ID Token Details" : "Active App ID Token Details";
        }

        return "API Usage Details";
    }

    const detailColumns = useMemo(() => {
        if (detailEntityType === "appIdToken") {
            return [
                {
                    Header: "Token ID",
                    accessor: "TokenId",
                    id: "tokenId",
                    Cell: ({ value }) => (<div>{value ?? "-"}</div>)
                },
                {
                    Header: "Version",
                    accessor: "Version",
                    id: "version",
                    Cell: ({ value }) => (<div>{value ?? "-"}</div>)
                },
                {
                    Header: "Project Name",
                    accessor: "ProjectName",
                    id: "projectName",
                    Cell: ({ value }) => (<div>{value || "-"}</div>)
                },
                {
                    Header: "Application Name",
                    accessor: "ApplicationName",
                    id: "applicationName",
                    Cell: ({ value }) => (<div>{value || "-"}</div>)
                },
                {
                    Header: "Company Name",
                    accessor: "CompanyName",
                    id: "companyName",
                    Cell: ({ value }) => (<div>{value || "-"}</div>)
                },
                {
                    Header: "Developer Name",
                    accessor: "DeveloperName",
                    id: "developerName",
                    Cell: ({ value }) => (<div>{value || "-"}</div>)
                },
                {
                    Header: "Created Date",
                    accessor: "CreatedDate",
                    id: "createdDate",
                    Cell: ({ value }) => (<div>{formatDate(value)}</div>)
                },
            ];
        }

        const columns_base = [
            {
                Header: "Project Name",
                accessor: "ProjectName",
                id: "projectName",
                Cell: ({ value }) => (<div>{value || "-"}</div>)
            },
            {
                Header: "Client ID",
                accessor: "ClientId",
                id: "clientId",
                Cell: ({ value }) => (<div>{value || "-"}</div>)
            },
            {
                Header: "Created Date",
                accessor: "CreatedDate",
                id: "createdDate",
                Cell: ({ value }) => (<div>{formatDate(value)}</div>)
            },
        ];

        if (detailType === "inactive") {
            columns_base.push({
                Header: "Deleted Date",
                accessor: "DeletedDate",
                id: "deletedDate",
                Cell: ({ value }) => (<div>{formatDate(value)}</div>)
            });
        }

        return columns_base;
    }, [detailEntityType, detailType]);

    const companyOptions = isArrayNotEmpty(allComList) ? allComList.map((obj) => {
        let comId = obj?.ComId ? obj.ComId : "";
        let comName = obj?.ComName ? obj?.ComName : "";
        return {
            label: comId + " - " + comName,
            value: comId
        };
    }) : [];

    function handleSearch() {
        if (!selectedCompanyId) {
            Toaster({ type: "info", msg: "Please select a Company ID" });
            return;
        }
        if (!startDate || !endDate) {
            Toaster({ type: "info", msg: "Please select Start Date and End Date" });
            return;
        }

        setCurrentView("summary");
    }

    let filterForm = (
        <div className='api-usage-report__content'>
            <div className={'api-usage-report__content-body'}>
                <div className={"row"}>
                    <div className='col-12 col-lg-4 pb-4'>
                        <Dropdown
                            options={companyOptions}
                            selectedValue_default={selectedCompanyId}
                            onChange={(value, label) => {
                                setSelectedCompanyId(value);
                            }}
                            label={"Company ID"}
                            placeholder={"Select Company"}
                            isLabelLegend
                            isSearchable
                            isInputDigitOnly={true}
                        />
                        <div className='pt-3'>
                            <DateRangePicker
                                handleOnChange_start={(date) => {
                                    setStartDate(date);
                                }}
                                handleOnChange_end={(date) => {
                                    setEndDate(date);
                                }}
                                label={"Date Range"}
                                placeholder_start={"Start Date"}
                                placeholder_end={"End Date"}
                                isLabelLegend
                                startDateOptions={{
                                    isMonthYear: false,
                                }}
                                endDateOptions={{
                                    isMonthYear: false,
                                }}
                            />
                        </div>
                    </div>
                    <div className='col-12 col-lg-2 pb-4 d-flex align-items-end'>
                        <Button
                            className={"search-button__input float-right float-lg-none"}
                            isLoading={getApiUsageReport_requesting}
                            isDisabled={getApiUsageReport_requesting}
                            handleOnClick={handleSearch}
                        >
                            Search
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );

    let tableDisplay = (
        <GeneralTable
            className={"api-usage-report__table"}
            columns={columns}
            data={userPermissions?.Internal_ApiUsage_View ? normalizedApiUsageList : []}

            getTableData={(pageNo, pageSize, sort) => {
                if (selectedCompanyId && startDate && endDate) {
                    const selectedCompanyId_formatted = Number(selectedCompanyId);
                    let filterOptions = {
                        companyIds: [Number.isNaN(selectedCompanyId_formatted) ? selectedCompanyId : selectedCompanyId_formatted],
                        startDate: moment(startDate).format("YYYY-MM-DD"),
                        endDate: moment(endDate).format("YYYY-MM-DD")
                    };
                    getApiUsageReport(pageNo, pageSize, sort, filterOptions);
                }
            }}

            outerTableHeader={(outerTableHeader_props) => {
                return (
                    <div className={"table__headers"}>
                        <div className={"headers__top-section d-flex justify-content-between align-items-center"}>
                            <div className={"headers__title tableHeaderTitle"}>
                                API Usage Report
                            </div>

                            <SubmenuTooltip
                                buttonClassName={"top-section__more-actions"}
                                menuItems={[
                                    {
                                        icon: ic_export,
                                        label: "Export",
                                        isDisabled: isArrayEmpty(normalizedApiUsageList),
                                        onClick: () => {
                                            Toaster({ type: "info", msg: "Export functionality coming soon" });
                                        },
                                    }
                                ]}
                            >
                                <MoreButton
                                    className={"more-actions__icon-wrapper"}
                                    iconClassName={"icon-wrapper__icon"}
                                />
                            </SubmenuTooltip>
                        </div>
                    </div>
                );
            }}

            isTableDataLoading={getApiUsageReport_requesting}

            isUsingPagination={false}
            totalRecords={getTotalCountFromHeader(apiUsageReportListHeaders)}

            isColumnResizeable
            isAllColumnWidthEqual
            isTableOddRowsDarken
        />
    );

    let detailPageDisplay = (
        <GeneralTable
            className={"api-usage-report__details-table"}
            columns={detailColumns}
            data={detailList}

            outerTableHeader={() => {
                const detailsTitle = getDetailTitle();

                return (
                    <div className={"table__headers"}>
                        <div className={"headers__top-section d-flex justify-content-between align-items-center"}>
                            <div className={"headers__title tableHeaderTitle"}>
                                {detailsTitle} {detailCompanyId ? `- Company ID ${detailCompanyId}` : ""}
                            </div>

                            <SubmenuTooltip
                                buttonClassName={"top-section__more-actions"}
                                menuItems={[
                                    {
                                        icon: ic_export,
                                        label: "Export",
                                        isDisabled: isArrayEmpty(detailList),
                                        onClick: exportDetailTable,
                                    }
                                ]}
                            >
                                <MoreButton
                                    className={"more-actions__icon-wrapper"}
                                    iconClassName={"icon-wrapper__icon"}
                                />
                            </SubmenuTooltip>
                        </div>
                    </div>
                );
            }}

            isUsingPagination={false}
            isColumnResizeable
            isAllColumnWidthEqual
            isTableOddRowsDarken
        />
    );

    return (
        <DashboardLayout
            headerTitle={currentView === "details"
                ? getDetailTitle()
                : "API Usage Report"}
            bodyClassName={"api-usage-report"}
            isHeaderCloseIconVisible={currentView !== "filter"}
            updateParentState_visiblity={currentView !== "filter" ? handleClosePage : null}
            body={currentView === "filter"
                ? filterForm
                : (currentView === "summary" ? tableDisplay : detailPageDisplay)}
        />
    );
}
